import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getUserDetails } from "../../../api/lead";
import {
  AlertComponent,
  BreadcrumbComponent,
  MUITable,
  PaginationComponent,
} from "../../../common/components";
import { useSetLoading } from "../../../common/hooks/appearance";
import { getCurrentTenantId } from "../../../utils/utils";
import { breadcrumbLink } from "./user-requests.constant";
import _ from "lodash";
import { useLogin } from "../../../common/hooks/useUser";
import { Box } from "@mui/system";
import icons from "../../../assets/icons/icons";
import { getTenetRoles } from "../../../api/tenets";
import { AlertMessage } from "../../tenant/tenant-details/tenant-details-types";
import UserRequestsListFilter from "./user-requests-filter/user-requests-filter";
import UserRequestsOptions from "./user-requests-filter/user-requests-filter-options/user-requests-filter-options";
import "./user-requests.scss";
import UserRequestsAddUser from "./user-requests-add-user/user-requests-add-user";
interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}
export interface UserRequestsListParams {
  limit: string | number;
  offset: string | number;
  sortDirection?: string | null;
  sortBy?: string | null;
  searchText?: string;
}
const UserRequests: React.FC = () => {
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [pageSize] = useState<number>(15);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [companyfilters, setCompanyFilters] = useState<any>({});
  const [searchText, setSearchText] = useState<string>("");
  const [crumbMappingOption, setCrumbMappingOption] =
    useState<any>(breadcrumbLink);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [tenantRoles, setTenantRoles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [tenantId, setTenantId] = useState<number | null>(getCurrentTenantId());
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [company, setCompany] = useState<any>();
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const tenantDetails = useLogin();
  const isuserLimitExceeded = useMemo(() => {
    return tenantDetails?.userLimitExceeded;
  }, [tenantDetails?.tenantDetails?.tenantType]);
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);
  const tenantName = tenantDetails?.tenantDetails?.companyName;
  const [alert, setAlert] = useState<{
    isAddOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const toggle = () => {
    setIsAddOpen(!isAddOpen);
    setCurrentPage(1);
  };
  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      sortValues = { sortDirection: sortType.toLowerCase(), sortBy: column };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };
  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading partners...";
    }
    if (searchText) {
      return "No users found matching your search criteria.";
    }
    return "No users found";
  }, [isLoading, searchText]);

  const headers = useMemo(
    () => [
      { label: "First Name", key: "first_name", isSortable: true },
      { label: "Last Name", key: "last_name", isSortable: true },
      { label: "Phone Number", key: "phone_number", isSortable: true },
      { label: "Email ID", key: "email", isSortable: true },
      {
        label: "Add User",
        key: "",
        valueGetter: (row: any) => {
          return (
            <span
              onClick={() => {
                setSelectedRow(row);
                setIsAddOpen(!isAddOpen);
              }}
              className="add-user-span"
            >
              Add User
            </span>
          );
        },
      },
    ],
    []
  );

  const fetchUsers = async (page: number = 1, search: string = "") => {
    try {
      setIsLoading(true);
      const offset = (page - 1) * pageSize;
      let params: UserRequestsListParams = {
        limit: pageSize,
        offset: offset,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      if (Object.keys(activeFilters).length) {
        params = { ...params, ...activeFilters };
      }
      const response = await getUserDetails(
        { ...params, allUsers: true, status: "REQUESTED_ACCESS" },
        "User Requests"
      );
      setData(response.data.data.data.data);
      setTotalRecords(response.data.data.data.totalRecords);
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error fetching user profile:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  const handleSearch = useCallback(
    _.debounce((text: string) => {
      setSearchText(text);
      setCurrentPage(1);
    }, 500),
    []
  );
  const tenantCurrentId = activeFilters ? tenantId : getCurrentTenantId();

  const getRole = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: any = await getTenetRoles({ tenantId: tenantCurrentId });
      if (response?.data?.success) {
        const roleOptions = response?.data?.data
          ?.map((item: any) => ({
            value: item?.id,
            label: item?.role,
          }))
          .filter((item: any) => item?.label !== "Super Admin");

        setTenantRoles(roleOptions);
      }
    } catch (error: any) {
      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "Something went wrong!",
        type: "error",
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [company]);
  useEffect(() => {
    getRole();
  }, [getRole]);
  useEffect(() => {
    fetchUsers(currentPage, searchText ?? "");
  }, [currentPage, searchText, sortOption, activeFilters, tenantId, setData]);
  useEffect(() => {
    if (!data?.length) return;
    const item = data[0];
    setCompany(item.companyName ?? tenantName);
    setTenantId(item.tenantId ?? getCurrentTenantId());
  }, [data]);
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.onBoardedAtEnd || activeFilters.onBoardedAtStart;

    const otherFilters = { ...activeFilters };
    delete otherFilters.onBoardedAtEnd;
    delete otherFilters.onBoardedAtStart;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);
  useEffect(() => {
    if (!selectedUserId || !data || data?.length === 0) return;
    const userDetails = data.find((user) => user.id === selectedUserId);
    const role = tenantRoles.find((r) => r.value === userDetails?.role_id);

    const updatedUserDetails = {
      ...userDetails,
      role: role,
    };
    setSelectedUserDetails(updatedUserDetails || null);
  }, [selectedUserId, data]);
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);
  return (
    <>
      <div className="user-requests-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={crumbMappingOption} />
        </div>
        <div>
          <div>
            <div>
              <UserRequestsListFilter
                isOpenFilter={isOpenFilter}
                setIsOpenFilter={setIsOpenFilter}
                onSearch={handleSearch}
                selectedUserRequestsListFilter={appliedFilterCount}
                onApplyFilter={(filterData: any) => {
                  setActiveFilters(filterData);
                }}
              />
            </div>
            {isuserLimitExceeded && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Box className="alert-users-limit">
                  <img src={icons.TermOfServicesInfo} alt="img" />
                  <p>
                    Your CRM plan's user limit has been reached. Upgrade or
                    remove users to proceed.
                  </p>
                </Box>
              </Box>
            )}
            <div className="user-requests-list-table-filter">
              <div className="user-requests-list-table">
                <MUITable
                  headers={headers}
                  data={data}
                  onSort={handleSort}
                  noResultsTxt={noResultsTxt}
                />
              </div>
              {isOpenFilter && (
                <UserRequestsOptions
                  open={isOpenFilter}
                  filters={activeFilters}
                  onClose={() => setIsOpenFilter(false)}
                  roles={tenantRoles}
                  onApplyFilters={(filterData, shouldClose = true) => {
                    try {
                      setActiveFilters(filterData);
                      if (shouldClose) setIsOpenFilter(false);
                      setCurrentPage(1);
                    } catch (error: any) {
                      console.error("Failed to apply filters:", error);
                      setAlertMessage({
                        open: true,
                        message: "Failed to apply filters. Please try again.",
                        type: "error",
                      });
                    }
                  }}
                />
              )}
            </div>
            {totalRecords && totalRecords > 0 ? (
              <div className="user-requests-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
          {alertMessage.open && (
            <AlertComponent
              message={alertMessage.message}
              type={alertMessage.type || "info"}
              open={alertMessage.open}
              onClose={() => setAlertMessage({ open: false })}
            />
          )}
        </div>
      </div>
      {isAddOpen && (
        <div className="user-requests-add-user-overlay">
          <UserRequestsAddUser
            roles={tenantRoles}
            onSuccess={fetchUsers}
            setIsAddOpen={toggle}
            rowData={selectedRow}
          />
        </div>
      )}
    </>
  );
};
export default UserRequests;
